import { SWITCHABLE_MODULES, type SwitchableModule } from '@repo/permissions'
import { atomWithStorage } from 'jotai/utils'
import { store } from '.'

export type ActiveModule = SwitchableModule

const MODULE_STORAGE_KEY = 'fuze_active_module'

export const MODULE_CONFIG = {
  core: { defaultPath: '/welcome', labelKey: 'moduleSwitcher.app' },
  crt: { defaultPath: '/crt/insights', labelKey: 'moduleSwitcher.crt' },
  credit: { defaultPath: '/credit/welcome', labelKey: 'moduleSwitcher.credit' },
} as const satisfies Record<SwitchableModule, { defaultPath: string; labelKey: string }>

export const activeModuleAtom = atomWithStorage<ActiveModule>(
  MODULE_STORAGE_KEY,
  'core',
  undefined,
  {
    getOnInit: true,
  },
)

export const getActiveModule = () => store.get(activeModuleAtom)

/**
 * The module a page belongs to, from its path.
 *
 * The route tree encodes it: CRT pages live under `/crt`, Credit pages under `/credit`, and the Fuze
 * app's own pages sit at the root. That makes the current path a more truthful answer than the
 * persisted active module, which only changes when someone uses the module switcher and so goes
 * stale the moment a deep link or bookmark crosses into another module.
 */
export function getModuleFromPath(pathname: string): SwitchableModule {
  if (pathname === '/crt' || pathname.startsWith('/crt/')) return 'crt'
  if (pathname === '/credit' || pathname.startsWith('/credit/')) return 'credit'

  return 'core'
}

/**
 * The module to land in when no page was asked for.
 *
 * The persisted active module wins while the user still has access to it. Otherwise the Fuze app
 * when they hold it, else the first module they do hold. `null` when they hold none, which the
 * caller leaves to the unverified guard.
 */
export function resolveHomeModule(
  storedModule: unknown,
  accessibleModules: readonly SwitchableModule[],
): SwitchableModule | null {
  const stored = accessibleModules.find((mod) => mod === storedModule)
  if (stored) return stored
  if (accessibleModules.includes('core')) return 'core'

  return SWITCHABLE_MODULES.find((mod) => accessibleModules.includes(mod)) ?? null
}
